// Shared className fragments for the storefront design system. Centralized so
// every page/component that needs a focus ring, form input, button or page
// shell stays pixel-identical without copy-pasting the same string in 15+ files.
//
// Two naming families exist and are deliberately kept in sync here:
//   * `btnPrimary` / `btnMd` / … — composable strings applied directly in JSX.
//   * `primaryButtonClass` / …   — consumed by the <Button> primitive.
// The second family aliases the first, so a raw `btnPrimary` button and a
// <Button variant="primary"> render identically. Sizing lives in one place
// (the btn* size tokens) for both.

export const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded';

// ─── Buttons ─────────────────────────────────────────────────────────────────
// Height comes from the size tokens (`h-*`) rather than vertical padding, so a
// button's box is the same whatever its font-size, and a field (h-12) lines up
// with the submit button beside it.
export const btnBase =
  `btn-glow inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-all disabled:opacity-50 disabled:pointer-events-none ${focusRing}`;

/** Filled brand CTA: Visit Store, Add to Cart, Sign In, Place Order. */
export const btnPrimary = `${btnBase} bg-gradient-primary text-white shadow-glow-teal`;
/** Outlined action sharing the primary's geometry. */
export const btnSecondary = `${btnBase} bg-white text-emerald-800 border border-emerald-200 shadow-soft hover:bg-emerald-50`;
/** Accent CTA for actions layered over imagery, where the green reads poorly. */
export const btnAccent = `${btnBase} bg-gradient-accent text-white shadow-glow`;
/** Quiet tertiary action with no border or fill. */
export const btnGhost = `${btnBase} text-emerald-800 hover:bg-emerald-50`;

// Sizes — one of these always accompanies a variant above.
/** Compact in-card action, e.g. a product card's Add button. */
export const btnXs = 'h-9 px-3.5 text-xs';
export const btnSm = 'h-10 px-5 text-sm';
export const btnMd = 'h-11 px-6 text-sm';
export const btnLg = 'h-12 px-8 text-base';

// Aliases consumed by components/Button.jsx (which supplies its own size).
export const primaryButtonClass = btnPrimary;
export const secondaryButtonClass = btnSecondary;
export const ghostButtonClass = btnGhost;

// ─── Forms ───────────────────────────────────────────────────────────────────
// `h-12` matches btnLg so a field and its submit button align in a row.
export const inputClass =
  `w-full h-12 px-4 rounded-xl border border-emerald-100 bg-white text-sm text-emerald-950 placeholder:text-ink-secondary shadow-soft transition-colors focus:border-emerald-700 ${focusRing}`;

/** Same box as `inputClass`, for <select>. */
export const selectClass =
  `h-12 px-4 rounded-xl border border-emerald-100 bg-white text-sm font-medium text-gray-700 shadow-soft transition-colors focus:border-emerald-700 ${focusRing}`;

/** Pill-shaped search field, with room for the leading search icon. */
export const searchInputClass =
  `w-full h-12 pl-12 pr-4 rounded-full border border-emerald-100 bg-white text-sm text-emerald-950 placeholder:text-ink-secondary shadow-card transition-colors focus:border-emerald-700 ${focusRing}`;

export const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

export const errorTextClass = 'text-sm text-red-600';

// ─── Page structure ──────────────────────────────────────────────────────────
// The navbar is fixed (12px offset + 72px bar = 84px, see config/navigation.js),
// so a page's first section must reserve more top padding than that or its
// heading renders underneath the header. Bottom padding scales with viewport;
// top padding never drops below the header's height.
export const pageSection =
  'pt-28 sm:pt-32 pb-16 sm:pb-20 lg:pb-24 min-h-[60vh] bg-gradient-to-b from-white to-canvas';

/** Spacing for sections that follow a first/hero section (no header to clear). */
export const sectionSpacing = 'py-16 sm:py-20 lg:py-24';

/** Centred single-card shell shared by every auth page. */
export const authSection =
  'hero-gradient pt-28 sm:pt-32 pb-16 sm:pb-20 min-h-[70vh] flex items-center';

// Containers. Gutters are identical across all five; only the measure differs,
// chosen by content: grids get the full width, forms stay narrow.
const gutters = 'mx-auto px-4 sm:px-6 lg:px-8';
/** Catalog grids and marketing sections. */
export const containerWide = `max-w-7xl ${gutters}`;
/** Two-column product detail. */
export const containerProduct = `max-w-6xl ${gutters}`;
/** Cart's item-list + summary grid. */
export const containerContent = `max-w-5xl ${gutters}`;
/** Single-column account/checkout/order pages. */
export const containerNarrow = `max-w-3xl ${gutters}`;
/** Auth forms. */
export const containerForm = `max-w-md ${gutters}`;

// ─── Typography ──────────────────────────────────────────────────────────────
// One hierarchy, scaling at the same breakpoint. Playfair (`serif`) carries
// every heading level; the homepage Hero keeps a larger display size on purpose.
/** Standard page title. */
export const pageHeading = 'serif text-3xl sm:text-4xl text-emerald-950';
/** Title inside the hero band of an editorial page (Support, Privacy). */
export const heroPageHeading = 'serif text-4xl md:text-5xl text-emerald-950 leading-tight';
/** Section title within a page. */
export const sectionHeading = 'serif text-3xl md:text-4xl text-emerald-950';
/** Title inside a card/panel. */
export const panelHeading = 'serif text-xl text-emerald-950';

// ─── Cards ───────────────────────────────────────────────────────────────────
/** Grid item: product, category, cart line. */
export const itemCard = 'bg-white rounded-2xl border border-emerald-100 shadow-soft';
/** Panel/surface holding a form or a summary. */
export const panelCard = 'bg-white rounded-3xl border border-emerald-50 shadow-card';

/** Standard elevated white card shell shared by product/category/feature cards. */
export const cardShellClass = `card-hover ${itemCard} overflow-hidden`;

/** Small pill used for freshness/quality/status callouts on cards. */
export const badgeClass = 'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold';
